// Вид карты (центр + зум) в query-параметрах — общий для всех разделов с картой.
//
// Раньше эта логика жила inline в MapsPage, поэтому «Модели» её не получили и вид
// там не сохранялся. Держим её здесь, чтобы страницы не расходились: новому разделу
// с картой достаточно взять useMapView() и раскидать пропсы в MapCanvas.

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useViewCtx } from '../context/ViewContext'

export interface MapView {
  lng: number | null
  lat: number | null
  zoom: number | null
}

export const EMPTY_VIEW: MapView = { lng: null, lat: null, zoom: null }

/** ~11 м по координатам и 2 знака по зуму: короткий URL без дрожания на моргании карты. */
export function roundView(v: { lng: number; lat: number; zoom: number }): MapView {
  return {
    lng: Math.round(v.lng * 1e4) / 1e4,
    lat: Math.round(v.lat * 1e4) / 1e4,
    zoom: Math.round(v.zoom * 100) / 100,
  }
}

export function parseMapView(params: URLSearchParams): MapView {
  const num = (v: string | null): number | null => (v == null || v === '' ? null : Number(v))
  return { lng: num(params.get('lng')), lat: num(params.get('lat')), zoom: num(params.get('zoom')) }
}

/** Дописывает вид к параметрам страницы, не затирая чужие (model/species/…). */
export function applyMapView(params: URLSearchParams, view: MapView): URLSearchParams {
  const out = new URLSearchParams(params)
  for (const key of ['lng', 'lat', 'zoom'] as const) {
    const value = view[key]
    if (value == null) out.delete(key)
    else out.set(key, String(value))
  }
  return out
}

export function sameView(a: MapView, b: MapView): boolean {
  return a.lng === b.lng && a.lat === b.lat && a.zoom === b.zoom
}

/** Вид, с которым раздел открывается: ключи из URL сильнее памяти, недостающие
 *  берутся из последнего вида сессии.
 *
 *  Зачем память вообще: переход по рельсу уводит на `/models` голым путём, без
 *  query, — а вид жил ТОЛЬКО в query. Дата·глубина·вид переживали переход (они в
 *  ViewContext над маршрутами), регион карты — нет, и каждый раздел открывался на
 *  дефолтном центре (замечено владельцем 2026-08-12). */
export function viewOnEnter(fromUrl: MapView, remembered: MapView): MapView {
  return {
    lng: fromUrl.lng ?? remembered.lng,
    lat: fromUrl.lat ?? remembered.lat,
    zoom: fromUrl.zoom ?? remembered.zoom,
  }
}

/** Вид карты, синхронизированный с URL: отдаёт пропсы для MapCanvas.
 *  Пан/зум → moveend → запись в URL (replace, без no-op) → ссылка шарится и переживает F5. */
export function useMapView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { recallMapView, rememberMapView } = useViewCtx()
  // Вход в раздел: ключи из ссылки сильнее, остальное — из памяти сессии, иначе
  // переход по рельсу (голый путь без query) сбрасывал бы регион на дефолт.
  const [view, setView] = useState<MapView>(() =>
    viewOnEnter(parseMapView(searchParams), recallMapView()),
  )

  useEffect(() => {
    rememberMapView(view)
    setSearchParams(prev => applyMapView(prev, view), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  const onMoveEnd = useCallback((v: { lng: number; lat: number; zoom: number }) => {
    const next = roundView(v)
    setView(prev => (sameView(prev, next) ? prev : next))
  }, [])

  return {
    center:
      view.lng != null && view.lat != null ? ([view.lng, view.lat] as [number, number]) : undefined,
    zoom: view.zoom ?? undefined,
    onMoveEnd,
  }
}
